import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Shuffle,
  Calendar,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  Check,
} from "lucide-react";

export default function GenerateTeamsPanel({
  selectedPlayers,
  getSelectedLevantadores,
  gameDate,
  setGameDate,
  location,
  setLocation,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  handleGenerateTeams,
  isGenerating,
  generatedTeams,
  isPublished,
  handlePublishTeams,
  isPublishing,
  recentGameDays,
  setGeneratedTeams,
  setGameDate: setGameDateForRecent,
}) {
  return (
    <div className="space-y-6">
      <Card className="bg-card border-border/50 shadow-xl">
        <CardHeader>
          <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Shuffle className="h-5 w-5 text-primary" />
            Gerar Times
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Data do Jogo
            </Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="date"
                value={gameDate}
                onChange={(e) => setGameDate(e.target.value)}
                className="pl-10 bg-input/50"
                data-testid="game-date-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Localização
            </Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Ex: Ginásio Municipal"
                className="pl-10 bg-input/50"
                data-testid="game-location-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Início</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="pl-10 bg-input/50" data-testid="game-start-time-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Fim</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="pl-10 bg-input/50" data-testid="game-end-time-input" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {selectedPlayers.length === 18 ? (
                <CheckCircle className="h-4 w-4 text-green-400" />
              ) : (
                <AlertCircle className="h-4 w-4 text-yellow-400" />
              )}
              <span className="text-sm">18 jogadores selecionados</span>
            </div>
            <div className="flex items-center gap-2">
              {getSelectedLevantadores() >= 3 ? (
                <CheckCircle className="h-4 w-4 text-green-400" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-400" />
              )}
              <span className="text-sm">Mínimo 3 levantadores</span>
            </div>
          </div>

          <Button
            onClick={handleGenerateTeams}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_hsl(var(--primary)/0.3)] font-bold uppercase tracking-wider"
            disabled={selectedPlayers.length !== 18 || getSelectedLevantadores() < 3 || isGenerating}
            data-testid="generate-teams-btn"
          >
            <Shuffle className="mr-2 h-4 w-4" />
            {isGenerating ? "Gerando..." : "Gerar Times Equilibrados"}
          </Button>

          {generatedTeams && !isPublished && (
            <Button
              onClick={handlePublishTeams}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(34,197,94,0.3)]"
              disabled={isPublishing}
              data-testid="publish-teams-btn"
            >
              <Send className="mr-2 h-4 w-4" />
              {isPublishing ? "Publicando..." : "Publicar Times"}
            </Button>
          )}

          {isPublished && (
            <div className="flex items-center justify-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400">
              <Check className="h-5 w-5" />
              <span className="font-medium">Times publicados!</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Game Days */}
      {recentGameDays.length > 0 && (
        <Card className="bg-card border-border/50 shadow-xl">
          <CardHeader>
            <CardTitle className="text-lg font-bold tracking-tight flex items-center gap-2">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              Jogos Recentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentGameDays.map((gd) => (
                <div
                  key={gd.id}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary/30 cursor-pointer"
                  onClick={() => {
                    setGeneratedTeams(gd.generated_teams);
                    setGameDateForRecent(gd.date);
                  }}
                >
                  <span className="text-sm">{gd.date}</span>
                  <Badge variant="outline">3 times</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
